import { Link, useNavigate } from "react-router-dom";
import { Layers, Package } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../lib/api";
import { fmtDate, fmtDateOnly, num, rm, timeAgo } from "../lib/format";
import { Badge, PageLoader, statusColor } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";

interface DashboardData {
  todaySales: number;
  todayOrderCount: number;
  totalSales: number;
  salesDeltaPct: number | null;
  pendingReceipts: number;
  newUsersToday: number;
  totalUsers: number;
  openRaffles: number;
  liveAuctions: number;
  revenueChart: { date: string; total: number; marketplace: number; marketplaceFees: number }[];
  topProducts: { productId: string | null; name: string; unitsSold: number }[];
  lowStock: { id: string; sku: string; name: string; available: number; threshold: number }[];
  recentOrders: {
    id: string;
    orderNo: string;
    kind: string;
    status: string;
    total: number;
    customer: string;
    createdAt: string;
  }[];
  activity: { id: string; action: string; entity: string | null; entityId: string | null; at: string }[];
}

const KIND_LABEL: Record<string, string> = {
  STORE: "Shop",
  RAFFLE_WIN: "Raffle win",
  AUCTION_WIN: "Auction win",
  CREDIT_TOPUP: "Credit top-up",
  VAULT_SHIPMENT: "Vault shipment",
};

function KpiCard({
  label,
  value,
  hint,
  delta,
  to,
}: Readonly<{
  label: string;
  value: string;
  hint?: string;
  delta?: number | null;
  to?: string;
}>) {
  const inner = (
    <div className="flex h-full min-h-[5.5rem] w-full flex-col rounded-2xl border border-line bg-surface p-4 transition-colors hover:bg-white/[0.02]">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40">{label}</p>
      <p className="mt-1 font-display text-xl font-bold">{value}</p>
      <div className="mt-auto flex min-h-[1.25rem] items-center gap-2 pt-1 text-xs text-white/50">
        {hint && <span>{hint}</span>}
        {delta != null && (
          <span className={delta >= 0 ? "text-emerald-400" : "text-red-400"}>
            {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}% vs yesterday
          </span>
        )}
      </div>
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="block h-full w-full min-w-0">
        {inner}
      </Link>
    );
  }
  return <div className="h-full w-full min-w-0">{inner}</div>;
}

export function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api<DashboardData>("/dashboard"),
  });

  const topProductColumns: Column<DashboardData["topProducts"][0]>[] = [
    {
      key: "name",
      header: "Product",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => <span className="font-medium">{r.name}</span>,
    },
    {
      key: "units",
      header: "Units sold",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.unitsSold,
      render: (r) => <span className="tabular-nums">{num(r.unitsSold)}</span>,
    },
  ];

  const recentOrderColumns: Column<DashboardData["recentOrders"][0]>[] = [
    {
      key: "orderNo",
      header: "Order#",
      sortable: true,
      sortValue: (r) => r.orderNo,
      render: (r) => <span className="font-mono text-xs">{r.orderNo}</span>,
    },
    {
      key: "customer",
      header: "Customer",
      sortable: true,
      sortValue: (r) => r.customer,
      render: (r) => r.customer,
      hideSm: true,
    },
    {
      key: "kind",
      header: "Kind",
      sortable: true,
      sortValue: (r) => r.kind,
      exportValue: (r) => KIND_LABEL[r.kind] ?? r.kind,
      render: (r) => <span className="text-xs text-white/60">{KIND_LABEL[r.kind] ?? r.kind}</span>,
      hideSm: true,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <Badge color={statusColor(r.status)}>{r.status.replaceAll("_", " ")}</Badge>,
    },
    {
      key: "total",
      header: "Total",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.total,
      exportValue: (r) => r.total,
      render: (r) => <span className="font-display font-semibold text-brand-pink">{rm(r.total)}</span>,
    },
    {
      key: "created",
      header: "Created",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.createdAt,
      exportValue: (r) => fmtDate(r.createdAt),
      render: (r) => <span className="text-xs text-white/50">{fmtDate(r.createdAt)}</span>,
    },
  ];

  if (isLoading) return <PageLoader />;

  return (
    <div className="page-scroll space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:items-stretch">
        <KpiCard
          label="Today's sales"
          value={rm(data?.todaySales ?? 0)}
          hint={`${num(data?.todayOrderCount ?? 0)} paid orders · Total ${rm(data?.totalSales ?? 0)}`}
          delta={data?.salesDeltaPct}
        />
        <KpiCard label="Pending receipts" value={num(data?.pendingReceipts ?? 0)} to="/receipts" />
        <KpiCard
          label="New users today"
          value={num(data?.newUsersToday ?? 0)}
          hint={`Total ${num(data?.totalUsers ?? 0)} users`}
          to="/users"
        />
        <KpiCard
          label="Live mechanics"
          value={`${num(data?.openRaffles ?? 0)} / ${num(data?.liveAuctions ?? 0)}`}
          hint="Open raffles · Live auctions"
          to="/raffles"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <section className="rounded-2xl border border-line bg-surface p-4 xl:col-span-2">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-display text-sm font-semibold">Revenue (30 days)</h3>
            <div className="flex flex-wrap items-center gap-3 text-[10px] text-white/50">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-brand-pink" aria-hidden />
                All sales
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-brand-blue" aria-hidden />
                Marketplace GMV
              </span>
            </div>
          </div>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.revenueChart ?? []} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#dd54eb" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#5460ed" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="marketplaceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5460ed" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#5460ed" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }}
                  tickFormatter={(v: string) => v.slice(5)}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
                />
                <Tooltip
                  contentStyle={{
                    background: "#2a173e",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                  labelFormatter={(v) => fmtDateOnly(String(v))}
                  formatter={(value: number, name: string) => {
                    const labels: Record<string, string> = {
                      total: "All sales",
                      marketplace: "Marketplace GMV",
                    };
                    return [rm(value), labels[name] ?? name];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  name="total"
                  stroke="#dd54eb"
                  strokeWidth={2}
                  fill="url(#revenueFill)"
                />
                <Area
                  type="monotone"
                  dataKey="marketplace"
                  name="marketplace"
                  stroke="#5460ed"
                  strokeWidth={2}
                  fill="url(#marketplaceFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="rounded-2xl border border-line bg-surface p-4">
          <h3 className="font-display text-sm font-semibold">Low stock</h3>
          <p className="mt-0.5 text-xs text-white/50">Products at or below threshold</p>
          {!data?.lowStock.length ? (
            <p className="py-8 text-center text-xs text-white/40">All stocked up</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {data.lowStock.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-2 rounded-xl border border-line/80 bg-white/[0.02] px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium">{p.name}</p>
                    <p className="text-[10px] text-white/40">{p.sku}</p>
                  </div>
                  <Badge color="amber">
                    {p.available} / {p.threshold}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          <Link to="/inventory?tab=lowstock" className="mt-3 block text-center text-xs text-brand-pink hover:underline">
            View inventory →
          </Link>
        </section>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold">Top products (30d)</h3>
          </div>
          <DataTable
            columns={topProductColumns}
            rows={data?.topProducts}
            rowKey={(r) => r.productId ?? r.name}
            exportFilename="dashboard-top-products"
            empty={{ icon: Layers, title: "No sales yet", hint: "Paid orders will populate this list." }}
          />
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold">Recent orders</h3>
            <Link to="/orders" className="text-xs text-brand-pink hover:underline">
              View all →
            </Link>
          </div>
          <DataTable
            columns={recentOrderColumns}
            rows={data?.recentOrders}
            rowKey={(r) => r.id}
            exportFilename="dashboard-recent-orders"
            onRowClick={() => navigate("/orders")}
            empty={{ icon: Package, title: "No orders yet" }}
          />
        </div>
      </div>

      <section className="rounded-2xl border border-line bg-surface p-4">
        <h3 className="font-display text-sm font-semibold">Activity</h3>
        <p className="mt-0.5 text-xs text-white/50">Latest staff actions</p>
        {!data?.activity?.length ? (
          <p className="py-8 text-center text-xs text-white/40">No recent activity</p>
        ) : (
          <ul className="mt-3 divide-y divide-line/60">
            {data.activity.map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="text-xs font-medium">{a.action}</p>
                  <p className="truncate text-[10px] text-white/40">
                    {a.entity ?? "—"}
                    {a.entityId && ` · ${a.entityId.slice(0, 8)}…`}
                  </p>
                </div>
                <span className="shrink-0 text-[10px] text-white/40">{timeAgo(a.at)}</span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/audit" className="mt-3 block text-center text-xs text-brand-pink hover:underline">
          Full audit log →
        </Link>
      </section>
    </div>
  );
}
